import { Box } from "@mui/material";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { SITE_LINK_ARROW_STYLES } from "../siteKit/siteButtonStyles";
import { revealRef } from "../siteKit/motion/reveal";
import type { NewsArticle } from "./newsArticles";
import { NewsImage } from "./NewsImage";
import { NEWS_LIST_STYLES, NEWS_META_STYLES } from "./newsStyles";

export interface NewsListItemProps {
  article: NewsArticle;
  /** 게재일 표시 문자열(호출부가 언어에 맞춰 만든다) */
  formattedDate: string;
  /** 목록 맨 위 기사: 사진을 페이지를 열자마자 받는다(press-news) */
  priority?: boolean;
}

const ARROW_PATH = "M6 3l5 5-5 5";
/** 사진 칸이 화면에 보이는 폭 — 900px 이하는 한 칸 전폭, 넓은 화면은 행의 왼쪽 칸(최대 약 640px) */
const LIST_IMAGE_SIZES = "(max-width:900px) 100vw, 640px";

/**
 * 언론보도 목록의 대표 기사형 행(시안 `.feat`): 왼쪽 큰 사진, 오른쪽 출처·날짜·제목·요약·「기사 읽기」.
 * 사진 설명·사용권 표기는 싣지 않는다 — 상세 본문 사진 아래에서 원문과 함께 보인다(press-news).
 * 행 전체가 사이트 안 상세(`/news/[id]`)로 가는 링크다(press-news).
 */
export function NewsListItem({ article, formattedDate, priority = false }: NewsListItemProps) {
  const { t } = useTranslation("landing");
  return (
    <Box
      component={Link}
      href={`/news/${article.id}`}
      ref={revealRef}
      data-reveal="up"
      aria-label={t("site.news.openLabel", { title: article.title })}
      sx={NEWS_LIST_STYLES.feature}
    >
      <Box data-news-image="" sx={NEWS_LIST_STYLES.image}>
        <NewsImage
          src={article.thumbnail}
          srcSet={article.thumbnailSrcSet}
          sizes={LIST_IMAGE_SIZES}
          alt={article.title}
          fallbackText={article.title}
          priority={priority}
        />
      </Box>
      <div>
        <Box component="p" sx={NEWS_META_STYLES}>
          {article.source} · {formattedDate}
        </Box>
        <Box component="h2" sx={NEWS_LIST_STYLES.title}>
          {article.title}
        </Box>
        <Box component="p" sx={NEWS_LIST_STYLES.summary}>
          {article.summary}
        </Box>
        <Box component="span" sx={{ ...SITE_LINK_ARROW_STYLES, ...NEWS_LIST_STYLES.more }}>
          {t("site.news.readMore")}
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d={ARROW_PATH}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </div>
    </Box>
  );
}
